import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bell,
  Check,
  Trash2,
  Users,
  UserPlus,
  Vote,
  Calendar,
  Wallet,
  CreditCard,
  CircleCheckBig,
  MessageCircle,
  Plane,
  Heart,
  MapPin,
  BookOpen,
  type LucideIcon,
} from "lucide-react";
import { Button, Popover, PopoverContent, PopoverTrigger, Badge } from "@tabipla/ui";
import type { NotificationResponseDto, NotificationType } from "@tabipla/shared";

// Default icon mapping - apps can override specific icons
const defaultNotificationIcons: Record<NotificationType, LucideIcon> = {
  group_invite: Users,
  member_joined: UserPlus,
  poll_created: Vote,
  poll_closed: Vote,
  schedule_created: Calendar,
  schedule_response: Calendar,
  expense_added: Wallet,
  chat_message: MessageCircle,
  trip_updated: Plane,
  trip_invite: Plane,
  trip_reminder: Plane,
  spot_added: MapPin,
  payment_request: CreditCard,
  payment_completed: CircleCheckBig,
  new_follower: Heart,
  spot_liked: Heart,
  booklet_liked: BookOpen,
  settlement_reminder: Wallet,
  poll_deadline_reminder: Vote,
};

function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "たった今";
  if (diffMins < 60) return `${diffMins}分前`;
  if (diffHours < 24) return `${diffHours}時間前`;
  if (diffDays < 7) return `${diffDays}日前`;
  return date.toLocaleDateString("ja-JP", { month: "short", day: "numeric" });
}

interface NotificationItemProps {
  notification: NotificationResponseDto;
  onRead: (id: string) => void;
  onDelete: (id: string) => void;
  onNavigate: (notification: NotificationResponseDto) => void;
  icons: Record<NotificationType, LucideIcon>;
}

function NotificationItem({
  notification,
  onRead,
  onDelete,
  onNavigate,
  icons,
}: NotificationItemProps) {
  const Icon = icons[notification.type] || Bell;

  return (
    <div
      className={`group flex items-start gap-3 rounded-lg p-3 transition-colors hover:bg-accent/50 ${
        !notification.read ? "bg-primary/5" : ""
      }`}
    >
      <div
        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${
          !notification.read ? "bg-primary/10" : "bg-muted"
        }`}
      >
        <Icon
          className={`h-4 w-4 ${!notification.read ? "text-primary" : "text-muted-foreground"}`}
        />
      </div>
      <div className="min-w-0 flex-1">
        <button onClick={() => onNavigate(notification)} className="block w-full text-left">
          <p className={`text-sm ${!notification.read ? "font-medium" : ""}`}>
            {notification.title}
          </p>
          <p className="line-clamp-2 text-xs text-muted-foreground">{notification.message}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {formatTimeAgo(notification.createdAt)}
          </p>
        </button>
      </div>
      <div className="flex flex-shrink-0 gap-1 opacity-0 transition-opacity group-hover:opacity-100">
        {!notification.read && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => {
              e.stopPropagation();
              onRead(notification.id);
            }}
          >
            <Check className="h-3 w-3" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(notification.id);
          }}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

export interface NotificationBellProps {
  /** List of notifications to display */
  notifications: NotificationResponseDto[];
  /** Count of unread notifications */
  unreadCount: number;
  /** Callback when marking a single notification as read */
  onMarkRead: (id: string) => void;
  /** Callback when marking all notifications as read */
  onMarkAllRead: () => void;
  /** Callback when deleting a notification */
  onDelete: (id: string) => void;
  /** Callback when clicking on a notification */
  onNavigate: (notification: NotificationResponseDto) => void;
  /** Whether mark all read action is pending */
  isMarkAllReadPending?: boolean;
  /** Custom icon mapping - merged with defaults */
  notificationIcons?: Partial<Record<NotificationType, LucideIcon>>;
}

export function NotificationBell({
  notifications,
  unreadCount,
  onMarkRead,
  onMarkAllRead,
  onDelete,
  onNavigate,
  isMarkAllReadPending = false,
  notificationIcons,
}: NotificationBellProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Merge custom icons with defaults
  const icons: Record<NotificationType, LucideIcon> = {
    ...defaultNotificationIcons,
    ...notificationIcons,
  };

  const handleNavigate = (notification: NotificationResponseDto) => {
    onNavigate(notification);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center p-0 text-xs"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="font-semibold">{t("notifications.title")}</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto px-2 py-1 text-xs"
              onClick={onMarkAllRead}
              disabled={isMarkAllReadPending}
            >
              {t("notifications.markAllRead")}
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Bell className="mb-2 h-8 w-8 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">{t("notifications.noNotifications")}</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onRead={onMarkRead}
                  onDelete={onDelete}
                  onNavigate={handleNavigate}
                  icons={icons}
                />
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
